/**
 * SellerRentalsDashboard — KPI tiles + overdue table.
 *
 * Renders at the top of the Rentals tab and refreshes whenever the
 * parent flips the `version` prop (e.g. after a return is marked).
 */
import React, { useEffect, useState, useCallback } from "react";
import { Package, Calendar, AlertTriangle, CheckCircle2,
         DollarSign, Tag, Phone, Mail, Send } from "lucide-react";
import { api } from "../../lib/api";
import { useSellerCurrency } from "../../lib/sellerCurrency";


function OverdueRow({ row, onReminderSent }) {
  const [busy, setBusy] = useState(false);
  const sendReminder = async () => {
    setBusy(true);
    try {
      await api.post(`/seller/rentals/${row.reservation_id}/reminder`);
      onReminderSent?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Reminder failed");
    } finally { setBusy(false); }
  };
  const lastRem = row.last_reminder_at
    ? new Date(row.last_reminder_at).toLocaleString()
    : null;
  return (
    <tr data-testid={`overdue-row-${row.reservation_id}`}>
      <td className="py-2 pr-3 font-bold">#{row.reservation_id.slice(-8).toUpperCase()}</td>
      <td className="py-2 pr-3">
        <div className="font-bold">{row.buyer_name || "—"}</div>
        <div className="flex flex-wrap gap-2 text-[10px] text-[#A1A1AA]">
          {row.buyer_email && (
            <a href={`mailto:${row.buyer_email}`} className="hover:text-[#00F0FF] flex items-center gap-0.5">
              <Mail size={9}/>{row.buyer_email}
            </a>
          )}
          {row.buyer_phone && (
            <a href={`tel:${row.buyer_phone}`} className="hover:text-[#00F0FF] flex items-center gap-0.5">
              <Phone size={9}/>{row.buyer_phone}
            </a>
          )}
        </div>
      </td>
      <td className="py-2 pr-3">{row.end_date}</td>
      <td className="py-2 pr-3 text-right font-bold text-[#FF007F]">+{row.days_overdue}d</td>
      <td className="py-2 pr-3 text-right">{row.items_count}</td>
      <td className="py-2 pr-3 text-right font-bold text-[#D4FF00]">${row.grand_total || 0}</td>
      <td className="py-2 text-right">
        <button onClick={sendReminder} disabled={busy}
          data-testid={`overdue-remind-${row.reservation_id}`}
          title={lastRem ? `Last sent: ${lastRem}` : "Send a return-please nudge"}
          className="btn-secondary text-[10px] px-2 py-1 inline-flex items-center gap-1">
          <Send size={10}/>
          {busy ? "…" : (row.reminders_sent > 0 ? `Remind (${row.reminders_sent})` : "Send reminder")}
        </button>
      </td>
    </tr>
  );
}


function KpiTile({ icon: Icon, label, value, color, sub, testid }) {
  return (
    <div className="card p-4 relative overflow-hidden" data-testid={testid}>
      <div className="absolute -right-4 -top-4 w-16 h-16 rounded-full opacity-[0.08]"
        style={{ background: color }}/>
      <div className="relative">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: `${color}1A` }}>
          <Icon size={14} style={{ color }}/>
        </div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mt-3">
          {label}
        </div>
        <div className="font-display font-black text-2xl mt-0.5" style={{ color }}>
          {value}
        </div>
        {sub && <div className="text-[10px] text-[#A1A1AA] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}


export default function SellerRentalsDashboard({ version }) {
  const { fmt } = useSellerCurrency();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/seller/rentals/stats");
      setStats(data);
    } catch (e) {
      setStats(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, version]);

  if (loading || !stats) {
    return <div className="card p-6 text-center text-[#A1A1AA] text-sm">Loading rental stats…</div>;
  }

  const bs = stats.by_status || {};

  return (
    <div className="space-y-4" data-testid="seller-rentals-dashboard">
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KpiTile icon={Tag} label="Rentable SKUs"
          value={stats.rentable_count} color="#D4FF00"
          testid="kpi-rentable"
          sub={`${stats.available_to_rent} available now`}/>
        <KpiTile icon={Package} label="Currently rented"
          value={stats.currently_rented} color="#00F0FF"
          testid="kpi-currently-rented"
          sub="Out of inventory today"/>
        <KpiTile icon={Calendar} label="Total bookings"
          value={stats.total_reservations} color="#FAFAFA"
          testid="kpi-total"
          sub="All time"/>
        <KpiTile icon={CheckCircle2} label="Returned"
          value={bs.returned || 0} color="#A1A1AA"
          testid="kpi-returned"
          sub="Completed safely"/>
        <KpiTile icon={AlertTriangle} label="Overdue"
          value={stats.overdue_count} color="#FF007F"
          testid="kpi-overdue"
          sub={stats.overdue_count > 0 ? "Needs action ↓" : "All on time"}/>
        <KpiTile icon={DollarSign} label="Revenue 30d"
          value={fmt(stats.revenue_30d || 0, { decimals: 0 })}
          color="#D4FF00"
          testid="kpi-revenue"
          sub="Rent + deposits"/>
      </div>

      {/* Overdue table — only render when there's stuff to do */}
      {stats.overdue_count > 0 && (
        <div className="card p-4" data-testid="overdue-table">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="text-[#FF007F]"/>
            <div className="font-display font-bold uppercase text-sm">
              Overdue reservations — needs follow-up
            </div>
            <span className="ml-auto text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded bg-[#FF007F]/15 text-[#FF007F]">
              {stats.overdue_count} pending
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold">
                <tr className="border-b border-[#3D2845]">
                  <th className="text-left py-2 pr-3">Reservation</th>
                  <th className="text-left py-2 pr-3">Buyer</th>
                  <th className="text-left py-2 pr-3">Due</th>
                  <th className="text-right py-2 pr-3">Days late</th>
                  <th className="text-right py-2 pr-3">Items</th>
                  <th className="text-right py-2 pr-3">Value</th>
                  <th className="text-right py-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3D2845]">
                {stats.overdue.map(o => (
                  <OverdueRow key={o.reservation_id} row={o}
                    onReminderSent={load}/>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
